import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { useSelector, useDispatch } from "react-redux";
import { setTriggerSignInSignUpModal } from "features/user/userSlice";

import {
  Grid,
  Typography,
  Button,
  Popper,
  Tooltip,
  makeStyles,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import InfoIcon from "@material-ui/icons/InfoOutlined";
import WorkIcon from "@material-ui/icons/Work";
import PromoIcon from "@material-ui/icons/MonetizationOn";
import { formatCurrency } from "utils/helpers";
import MemberTag from "components/MemberTag";
import { SIGN_IN_MODAL } from "utils/constants";
import { checkIsInIframe, requestParentToSignUp } from "utils/marketingSiteMessageUtils";

const useStyles = makeStyles((theme) => ({
  packageRow: {
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "nowrap",
  },
  packageRowVertical: {
    flexDirection: "column",
  },
  divider: {
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(3),
  },
  bookButton: {
    marginLeft: "10px",
  },
  bookButtonVerticalModeContainer: {
    alignSelf: "flex-end",
  },
  roomInfo: {
    background: theme.palette.info.main,
    color: theme.palette.info.contrastText,
    padding: "1.25rem",
    maxWidth: "80vw",
  },
  roomInfoContainer: {
    zIndex: 100000,
  },
  infoIcon: {
    color: theme.palette.text.primary,
    fontSize: "1.3rem",
    marginLeft: "10px",
    cursor: "pointer",
  },
  unitName: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  discountIcon: {
    marginTop: "-2px",
    marginRight: "4px",
    color: theme.palette.success.darkText,
  },
  redText: {
    color: theme.palette.text.red,
    fontWeight: 700,
  },
  memberRateInfoTag: {
    marginBottom: "8px",
  },
}));

export const AvailableUnit = ({
  showDivider,
  availability,
  onReservationSelect,
  stayNights,
  verticalMode,
  sideBarModalMode,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const theme = useTheme();
  const tabletUpScreen = useMediaQuery(theme.breakpoints.up("tablet"));
  const firebaseToken = useSelector((state) => state.user.firebaseToken);
  const authUser = useSelector((state) => state.user.authUser);

  const isMolliesMember = authUser?.emailVerified && !!firebaseToken;
  const isMemberRate = availability?.ratePlan?.isMemberRate || false;
  const shouldShowRateAndNights = isMolliesMember || !isMemberRate;
  const showMemberPriceToAll = availability?.ratePlan?.showPrice || false;
  const showJoinNow = !isMolliesMember && isMemberRate;

  const [roomInfoAnchor, setRoomInfoAnchor] = React.useState(null);
  const open = Boolean(roomInfoAnchor);
  const isInIframe = checkIsInIframe();

  const handlePopperOpen = (event) => {
    setRoomInfoAnchor(event.target);
  };

  const handlePopperClose = () => {
    setRoomInfoAnchor(null);
  };

  const handleJoinNowModal = () => {
    if (isInIframe) {
      requestParentToSignUp();
    } else {
      dispatch(setTriggerSignInSignUpModal(SIGN_IN_MODAL.SIGN_UP));
    }
  };

  return (
    <Grid
      container
      className={clsx(classes.packageRow, {
        [classes.packageRowVertical]: verticalMode,
        [classes.divider]: showDivider,
      })}
      style={sideBarModalMode ? { height: "100%" } : {}}>
      <Grid item>
        {isMemberRate ? (
          <MemberTag
            showTooltip
            className={classes.memberRateInfoTag}
            label={availability.ratePlan.name}
            tooltipTitle={isMolliesMember ? availability.ratePlan.description : ""}
            tooltipDescription={isMolliesMember ? availability.cancellationFee.description : ""}
          />
        ) : (
          <Typography variant="subtitle1" color="textPrimary" className={classes.unitName}>
            {availability.isCorporate ? (
              availability.corporateCode ? (
                <Tooltip title={`Partner: ${availability.corporateCode}`}>
                  <WorkIcon className={classes.discountIcon} fontSize="small" />
                </Tooltip>
              ) : (
                <Tooltip title="Promo">
                  <PromoIcon className={classes.discountIcon} fontSize="small" />
                </Tooltip>
              )
            ) : null}
            {availability.ratePlan.name}
            <InfoIcon
              className={classes.infoIcon}
              onMouseEnter={handlePopperOpen}
              onMouseLeave={handlePopperClose}
            />
            <Popper
              id="room-info"
              open={open}
              anchorEl={roomInfoAnchor}
              className={classes.roomInfoContainer}>
              <Grid className={classes.roomInfo} item>
                <Typography variant="body1">{availability.ratePlan.description}</Typography>
                <Typography variant="body1">{availability.cancellationFee.description}</Typography>
              </Grid>
            </Popper>
          </Typography>
        )}
        <Typography variant="body1" className={classes.redText}>
          {(shouldShowRateAndNights || showMemberPriceToAll) &&
            formatCurrency(
              availability.totalGrossAmount.currency,
              availability.totalGrossAmount.amount,
            )}
          {!isMolliesMember && isMemberRate && showMemberPriceToAll && "*"}

          {shouldShowRateAndNights || showMemberPriceToAll ? (
            <Typography variant="body2" color="textPrimary" component="span">
              {" "}
              for {stayNights} night{stayNights > 1 ? "s" : null}
            </Typography>
          ) : (
            <Typography variant="body2" color="textSecondary" component="span">
              Join to unlock your rate
            </Typography>
          )}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {availability.cancellationPolicy}
        </Typography>
      </Grid>
      <Grid
        item
        className={clsx({
          [classes.bookButtonVerticalModeContainer]: verticalMode,
        })}>
        {showJoinNow && (
          <Button
            className={classes.bookButton}
            variant="contained"
            color="secondary"
            onClick={handleJoinNowModal}>
            {tabletUpScreen || verticalMode ? "Join now" : "Join"}
          </Button>
        )}
        {(isMolliesMember || !isMemberRate) && (
          <Button
            className={classes.bookButton}
            variant="contained"
            color="primary"
            onClick={(e) => onReservationSelect(availability, e)}>
            {tabletUpScreen || verticalMode ? "Book room" : "Book"}
          </Button>
        )}
      </Grid>
    </Grid>
  );
};

AvailableUnit.propTypes = {
  showDivider: PropTypes.bool,
  availability: PropTypes.object,
  onReservationSelect: PropTypes.func,
  stayNights: PropTypes.number,
  verticalMode: PropTypes.bool,
  sideBarModalMode: PropTypes.bool,
};

export default AvailableUnit;
